import React, {FC, useState} from 'react';

import {Loader} from '@optimaxdev/design-system';
import {VaultedPaymentMethod} from '@primer-io/checkout-web';

import classNames from 'classnames';

import {connect} from 'react-redux';

import {useFeature} from 'components/feature';
import {defaultErrorState} from 'desktop/pages/checkout/components/paymentPrimerCreditCard/constants';
import {ApplicationStoreType} from 'desktop/store';

import {
    setSaveCardChecked as setSaveCardCheckedAction,
    setVaultPaymentId as setVaultPaymentIdAction,
} from '../../store/reducers/primer/primer';
import {
    getIsPrimerCreditCardLoading,
    getVaultedPaymentMethods,
} from '../../store/selectors/primer/primer';
import {PaymentCreditCartSkeleton} from '../paymentCreditCartSkeleton/paymentCreditCartSkeleton';
import {PaymentFooter} from '../paymentFooter/paymentFooter';

import {PrimerForm} from './components/primerForm/primerForm';
import {SaveCardCheckboxConnected} from './components/saveCardCheckbox/saveCardCheckbox';
import {SavedCardsSwitcher} from './components/savedCardsSwitcher/savedCardsSwitcher';
import {VaultedCardsConnected} from './components/vaultedCards/vaultedCards';

import {usePrimerCreditCard} from './hooks/usePrimerCreditCard';

import s from './paymentPrimerCreditCard.scss';

export type PropsType = {
    vaultedPaymentMethods: VaultedPaymentMethod[];
    setVaultPaymentId: (paymentId: string) => void;
    setSaveCardChecked: (isChecked: boolean) => void;
    isLoading: boolean;
};

const getPrimerCCLoader = (isFacelift: boolean) => {
    return (
        <>
            {isFacelift ? (
                <PaymentCreditCartSkeleton />
            ) : (
                <div aria-label="Loading credit card" className={s.loader}>
                    <Loader />
                </div>
            )}
        </>
    );
};

/**
 * returns Primer credit card payment method
 */
// eslint-disable-next-line max-statements
export const PaymentPrimerCreditCard: FC<PropsType> = ({
    isLoading,
    setSaveCardChecked,
    setVaultPaymentId,
    vaultedPaymentMethods,
}) => {
    const isFacelift = useFeature('dCheckoutFacelift');

    const [isReady, setIsReady] = useState(false);
    const {cardNetwork, primerError, setPrimerError, submitForm} = usePrimerCreditCard({
        onReady: () => setIsReady(true),
    });
    const [showSavedCards, setShowSavedCards] = useState(true);

    const onSavedCardToggle = () => {
        const newShowSavedCards = !showSavedCards;
        setVaultPaymentId('');
        setSaveCardChecked(false);
        setShowSavedCards(newShowSavedCards);
        setPrimerError(defaultErrorState);
    };

    const hasVaultedCards = vaultedPaymentMethods.length > 0;
    const showCardsListComponent = hasVaultedCards && showSavedCards;

    const renderCTAButton = () => <PaymentFooter isLoading={isLoading} onSubmit={submitForm} />;
    const renderPrimerError = () =>
        primerError && (
            <p className={s.error} id="error">
                {primerError}
            </p>
        );
    const renderVaultedCardsToggle = () =>
        hasVaultedCards && (
            <SavedCardsSwitcher isSavedCardsShown={showSavedCards} onChange={onSavedCardToggle} />
        );

    return (
        <>
            {isReady && showCardsListComponent && (
                <>
                    <VaultedCardsConnected vaultedPaymentMethods={vaultedPaymentMethods} />
                    {renderPrimerError()}
                    {renderVaultedCardsToggle()}
                    {renderCTAButton()}
                </>
            )}
            <div
                className={classNames({
                    [s.hide]: !isReady || showCardsListComponent,
                })}
            >
                <PrimerForm cardNetwork={cardNetwork} isReady={isReady} />
                {isReady && (
                    <>
                        <SaveCardCheckboxConnected />
                        {renderPrimerError()}
                        {renderVaultedCardsToggle()}
                        {renderCTAButton()}
                    </>
                )}
            </div>

            {!isReady && getPrimerCCLoader(isFacelift)}
        </>
    );
};

/**
 * Map state to props
 * @param {ApplicationStoreType} state - redux store
 */
const mapStateToProps = (state: ApplicationStoreType) => ({
    isLoading: getIsPrimerCreditCardLoading(state),
    vaultedPaymentMethods: getVaultedPaymentMethods(state),
});

export const PaymentPrimerCreditCardConnected = connect(mapStateToProps, {
    setSaveCardChecked: setSaveCardCheckedAction,
    setVaultPaymentId: setVaultPaymentIdAction,
})(PaymentPrimerCreditCard);
